import React from "react";
import { Box, Paper, Typography, Button, Chip, Popover, alpha } from "@mui/material";
import { ThemeProvider, createTheme } from "@mui/material/styles";
import AssignmentOutlinedIcon from "@mui/icons-material/AssignmentOutlined";
import DownloadOutlinedIcon from "@mui/icons-material/DownloadOutlined";
import PersonOutlineIcon from "@mui/icons-material/PersonOutline";
import { brandColors, brandThemeOptions } from "./brandColors";
import { useAppDispatch, useAppSelector } from "../../../store/hooks";
import { GetClaimTransactionDocumentContent } from "../../Dashboard/DashboardAction";
import { RootState } from "../../../store/store";
import { getBufferData } from "../../../utils/Utility";
import { setClaimTransactionDocumentContent } from "../../Dashboard/DashboardSlice";

interface PolicyAction {
    id: string;
    label: string;
    params?: any;
    followup_message?: string;
}

interface ClaimStatusCardProps {
    card: any;
    onAction?: (action: PolicyAction) => void;
}

const formatDisplayDate = (raw?: string) => {
    if (!raw) return undefined;
    const firstToken = raw.split(" ")[0];
    let d: Date | null = null;
    if (firstToken.includes("-")) {
        const [yyyy, mm, dd] = firstToken.split("-");
        if (yyyy && mm && dd) d = new Date(Number(yyyy), Number(mm) - 1, Number(dd));
    } else if (firstToken.includes("/")) {
        const [mm, dd, yyyy] = firstToken.split("/");
        if (yyyy && mm && dd) d = new Date(Number(yyyy), Number(mm) - 1, Number(dd));
    }
    if (!d || isNaN(d.getTime())) return undefined;
    const formatter = new Intl.DateTimeFormat(undefined, {
        month: "short",
        day: "2-digit",
        year: "numeric",
    });
    return formatter.format(d);
};

const normalize = (text?: string) => (text || "").toLowerCase().trim();
const findItemValue = (items: any[] = [], labels: string[]) => {
    for (const l of labels) {
        const found = (items || []).find((i) => normalize(i?.label) === normalize(l));
        if (found && (found.value || found["Value"])) return found.value || found["Value"];
    }
    for (const l of labels) {
        const found = (items || []).find((i) => normalize(i?.label).includes(normalize(l)));
        if (found && (found.value || found["Value"])) return found.value || found["Value"];
    }
    return undefined;
};

const ClaimStatusCard: React.FC<ClaimStatusCardProps> = ({ card, onAction }) => {
    
    const dispatch = useAppDispatch();
    const { isSuccess, isError, calledReducerType } = useAppSelector((s: RootState) => s.common);
    const { claimTransactionDocumentContent } = useAppSelector((s: RootState) => s.dashboard);

    const transactionsSection = (card?.sections || []).find(
        (s: any) => (s?.heading || "").toLowerCase().includes("transaction")
    );
    const documentsSection = (card?.sections || []).find(
        (s: any) => (s?.heading || "").toLowerCase().includes("document")
    );

    const claimId = card?.sections
        ?.find((section: any) => section.heading === "Claim Details")
        ?.items?.find((item: any) => item.label === "Claim ID")?.value;

    const [currentClickFile, setCurrentClickFile] = React.useState<{ name: string; addDate?: string; addTime?: string } | null>(null);
    const [anchorEl, setAnchorEl] = React.useState<HTMLElement | null>(null);

    const handleDownload = (doc: any) => {
        setCurrentClickFile({
            name: doc?.label || "document",
            addDate: typeof doc?.["Added On"] === "string" ? doc?.["Added On"] : undefined,
            addTime: (doc?.addTime as string) || undefined,
        });
        dispatch(setClaimTransactionDocumentContent(null));
        if (claimId) {
            dispatch(
                GetClaimTransactionDocumentContent({
                    claimId,
                    documentId: "Claims_Document",
                })
            );
        } else {
            alert("Claim ID not found");
        }
    };

    React.useEffect(() => {
        if (
            isSuccess &&
            calledReducerType === "dashboard/GetClaimTransactionDocumentContent" &&
            claimTransactionDocumentContent &&
            currentClickFile
        ) {
            const bufferArray = getBufferData(claimTransactionDocumentContent);
            const url = URL.createObjectURL(new Blob([bufferArray], { type: "application/pdf" }));
            const downloadLink = document.createElement("a");
            downloadLink.download = `${currentClickFile?.name}-${currentClickFile?.addDate}-${currentClickFile?.addTime}.pdf`;
            downloadLink.href = url;
            document.body.appendChild(downloadLink);
            downloadLink.click();
            document.body.removeChild(downloadLink);
            URL.revokeObjectURL(url);
            setCurrentClickFile(null);
        }
    }, [calledReducerType, isSuccess, claimTransactionDocumentContent, currentClickFile]);

    React.useEffect(() => {
        if (isError && calledReducerType === "dashboard/GetClaimTransactionDocumentContent") {
            alert(`Error in downloading document`);
        }
    }, [calledReducerType, isError]);

    const subtitle = card?.subtitle || "";
    const statusMatch = subtitle.match(/Status:\s*(\w+)/i);
    const status = statusMatch ? statusMatch[1].toLowerCase() : "";

    const statusMap: Record<string, { label: string; color: "default" | "warning" | "success" | "error" }> = {
        open: { label: "Open", color: "warning" },
        submitted: { label: "Submitted", color: "success" },
        closed: { label: "Closed", color: "error" },
    };
    const chipConfig = statusMap[status];
    if (!card) return null;

    // Extract contact person for use in both display and popover
    const detailsSection: any =
        (card?.sections || []).find((s: any) => {
            const heading = normalize(s?.heading);
            return heading === "claim details" || heading === "claim status";
        }) || {};
    const items = detailsSection.items || [];
    const contactPerson = findItemValue(items, ["Contact Person", "Contact", "Adjuster", "Name"]);

    return (
        <ThemeProvider theme={(outer) => createTheme(outer, brandThemeOptions)}>
        <Paper
            elevation={0}
            sx={{
                width: { xs: "100%", md: "100%" },
                border: "1px solid #eceff3",
                borderRadius: { xs: 2, md: 2.5 },
                p: { xs: 1.25, md: 1.75 },
                bgcolor: "#ffffff",
            }}
        >
            <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 1 }}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1, minWidth: 0 }}>
                    <Box
                        sx={{
                            width: { xs: 28, md: 32 },
                            height: { xs: 28, md: 32 },
                            borderRadius: "9px",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            color: "primary.main",
                            bgcolor: (theme) => alpha(theme.palette.primary.main, 0.12),
                            flexShrink: 0,
                        }}
                    >
                        <AssignmentOutlinedIcon sx={{ fontSize: { xs: 17, md: 19 } }} />
                    </Box>
                    <Typography sx={{ fontWeight: 700, fontSize: { xs: 12.5, md: 14 }, color: "#111827" }}>
                        {card?.title || "Track Claim"}
                    </Typography>
                </Box>
                {chipConfig && (
                    <Chip label={chipConfig.label} color={chipConfig.color} size="small" sx={{ fontWeight: 600, borderRadius: 9999, flexShrink: 0 }} />
                )}
            </Box>

            {/* Claim Details grid */}
            {(() => {
                const detailsSection: any =
                    (card?.sections || []).find((s: any) => {
                        const heading = normalize(s?.heading);
                        return heading === "claim details" || heading === "claim status";
                    }) || {};
                const items = detailsSection.items || [];
                const policyNumber = findItemValue(items, ["Policy Number", "Policy No", "Policy Id"]);
                const claimNumber = findItemValue(items, ["Claim Number", "Claim Id", "ClaimId"]);
                const claimInitiated = findItemValue(items, ["Claim Initiated", "Initiated On"]);
                const updatedOn = findItemValue(items, ["Updated On", "Updated", "Last Updated"]);
                const statusLabel = chipConfig?.label || findItemValue(items, ["Status"]) || "-";
                const labelStyle = {
                    fontSize: { xs: 9.5, md: 10.5 },
                    color: "#9ca3af",
                    fontWeight: 600,
                    textTransform: "uppercase",
                    letterSpacing: 0.4,
                } as const;
                const valueStyle = { fontWeight: 600, fontSize: { xs: 12.5, md: 14 }, color: "#111827" } as const;
                return (
                    <Box sx={{
                        display: "grid",
                        gridTemplateColumns: { xs: "1fr 1fr", sm: "1fr 1fr 1fr" },
                        gap: { xs: 1.25, md: 1.5 },
                        mt: 1.5,
                        p: { xs: 1.25, md: 1.5 },
                        borderRadius: 2,
                        border: "1px solid",
                        borderColor: (theme) => alpha(theme.palette.primary.main, 0.15),
                        bgcolor: (theme) => alpha(theme.palette.primary.main, 0.04),
                    }}>
                        {/* Left column */}
                        <Box>
                            <Typography sx={labelStyle}>Policy Number</Typography>
                            <Typography sx={valueStyle}>{policyNumber || "-"}</Typography>
                        </Box>
                        <Box>
                            <Typography sx={labelStyle}>Status</Typography>
                            <Typography sx={valueStyle}>{statusLabel}</Typography>
                        </Box>
                        <Box>
                            <Typography sx={labelStyle}>Claim Number</Typography>
                            <Typography sx={valueStyle}>{claimNumber || "-"}</Typography>
                        </Box>
                        <Box>
                            <Typography sx={labelStyle}>Updated On</Typography>
                            <Typography sx={valueStyle}>{formatDisplayDate(updatedOn) || updatedOn || "-"}</Typography>
                        </Box>
                        <Box>
                            <Typography sx={labelStyle}>Claim Initiated</Typography>
                            <Typography sx={valueStyle}>{formatDisplayDate(claimInitiated) || claimInitiated || "-"}</Typography>
                        </Box>
                        <Box>
                            <Typography sx={labelStyle}>Contact Person</Typography>
                            <Box
                                sx={{
                                    display: "inline-flex",
                                    alignItems: "center",
                                    gap: 0.5,
                                    cursor: contactPerson ? "pointer" : "default",
                                    color: contactPerson ? brandColors.primary : "#111827",
                                    "&:hover": contactPerson ? { textDecoration: "underline" } : undefined,
                                }}
                                onMouseEnter={(e) => contactPerson && setAnchorEl(e.currentTarget)}
                                onMouseLeave={() => setAnchorEl(null)}
                                onClick={(e) => contactPerson && setAnchorEl(e.currentTarget)}
                            >
                                {contactPerson && <PersonOutlineIcon sx={{ fontSize: { xs: 14, md: 15 } }} />}
                                <Typography
                                    sx={{
                                        ...valueStyle,
                                        color: "inherit",
                                    }}
                                >
                                    {String(contactPerson || "-")?.split(",")[1]?.split("|")?.[0]?.trim() || contactPerson || "-"}
                                </Typography>
                            </Box>
                        </Box>
                    </Box>
                );
            })()}

            {/* Track Your Claim heading with top border */}
            <Box sx={{ mt: 2 }}>
                <Box sx={{ borderTop: "1px solid #e5e7eb", mb: 0.75 }} />
                <Typography sx={{ fontWeight: 700, fontSize: { xs: 11, md: 12 }, color: "#374151" }}>
                    Track Your Claim
                </Typography>
            </Box>

            {/* Transactions */}
            <Box sx={{border: "1px solid #eceff3",
                p: { xs: 1, md: 1.5 },
                borderRadius: { xs: 2, md: 2.5 }, mt: 1}}>
            {transactionsSection && (
                <Box sx={{ mt: 1.5}}>
                    <Typography sx={{ fontWeight: 700, fontSize: { xs: 11, md: 12 }, color: "#374151", mb: 0.75 }}>
                        Transactions
                    </Typography>
                    <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                        {(transactionsSection.items || []).map((item: any, idx: number) => {
                            const isLast = idx === (transactionsSection.items.length - 1);
                            const dateText = formatDisplayDate(item.label) || item.label;
                            return (
                                <Box key={`txn_${idx}`} sx={{ display: "grid", gridTemplateColumns: "84px 32px 1fr", alignItems: "start" }}>
                                    <Typography sx={{ fontSize: { xs: 10, md: 12 }, color: "#6b7280", pt: "11px" }}>{dateText}</Typography>
                                    <Box sx={{ position: "relative", height: "100%", display: "flex", justifyContent: "center", minHeight: 40 }}>
                                        <Box sx={{ width: 2, bgcolor: (theme) => alpha(theme.palette.primary.main, 0.25), position: "absolute", top: idx === 0 ? 14 : 0, bottom: isLast ? "calc(100% - 14px)" : 0, left: "50%", transform: "translateX(-50%)" }} />
                                        <Box
                                            sx={{
                                                width: idx === 0 ? 12 : 10,
                                                height: idx === 0 ? 12 : 10,
                                                mt: "8px",
                                                bgcolor: idx === 0 ? "primary.main" : "#ffffff",
                                                border: "2px solid",
                                                borderColor: "primary.main",
                                                borderRadius: "50%",
                                                position: "relative",
                                                boxShadow: idx === 0 ? (theme) => `0 0 0 4px ${alpha(theme.palette.primary.main, 0.15)}` : "none",
                                            }}
                                        />
                                    </Box>
                                    <Box sx={{ pt: "8px", pb: 0.5 }}>
                                        <Typography sx={{ fontSize: { xs: 11, md: 12.5 }, color: "#1f2937", fontWeight: idx === 0 ? 600 : 500 }}>{String(item.value).replace(/\s*\(.*\)$/, "")}</Typography>
                                    </Box>
                                </Box>
                            );
                        })}
                    </Box>
                </Box>
            )}

            {/* Documents */}
            {documentsSection && (
                <Box sx={{ mt: 2 }}>
                    <Typography sx={{ fontWeight: 700, fontSize: { xs: 11, md: 12 }, color: "#374151", mb: 0.75 }}>
                        Documents
                    </Typography>
                    <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                        {(documentsSection.items || []).map((doc: any, idx: number) => {
                            const addedOn = formatDisplayDate(doc["Added On"]);
                            return (
                                <Paper
                                    key={`doc_${idx}`}
                                    elevation={0}
                                    sx={{
                                        border: "1px solid #eceff3",
                                        borderRadius: { xs: 2, md: 2.5 },
                                        p: { xs: 1, md: 1.25 },
                                        bgcolor: "#ffffff",
                                        display: "flex",
                                        alignItems: "center",
                                        justifyContent: "space-between",
                                        gap: 1,
                                        transition: "border-color 200ms ease, box-shadow 200ms ease",
                                        "&:hover": {
                                            borderColor: (theme) => alpha(theme.palette.primary.main, 0.4),
                                            boxShadow: (theme) => `0 6px 16px ${alpha(theme.palette.primary.main, 0.12)}`,
                                        },
                                    }}
                                >
                                    <Box sx={{ display: "flex", gap: 1, alignItems: "center", minWidth: 0 }}>
                                        <Box sx={{ width: 40, height: 40, borderRadius: 1.5, bgcolor: "#FDECEC", display: "flex", alignItems: "center", justifyContent: "center", color: "#991B1B", fontWeight: 700, fontSize: 12, flexShrink: 0 }}>PDF</Box>
                                        <Box sx={{ minWidth: 0 }}>
                                            <Typography sx={{ fontSize: { xs: 11, md: 12 }, color: "#111827", fontWeight: 600, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                                                {doc.label}
                                            </Typography>
                                            <Typography sx={{ fontSize: { xs: 10, md: 12 }, color: "#6b7280" }}>
                                                Date: {addedOn || "-"}
                                            </Typography>
                                        </Box>
                                    </Box>
                                    <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, flexShrink: 0 }}>
                                        <Button
                                            size="small"
                                            variant="outlined"
                                            startIcon={<DownloadOutlinedIcon />}
                                            onClick={() => handleDownload(doc)}
                                            sx={{
                                                textTransform: "none",
                                                fontSize: { xs: 10, md: 12 },
                                                fontWeight: 600,
                                                borderRadius: 9999,
                                                px: { xs: 1.25, md: 1.5 },
                                                borderColor: (theme) => alpha(theme.palette.primary.main, 0.5),
                                                color: "primary.main",
                                                "&:hover": {
                                                    borderColor: "primary.main",
                                                    bgcolor: (theme) => alpha(theme.palette.primary.main, 0.08),
                                                },
                                            }}
                                        >
                                            Download
                                        </Button>
                                    </Box>
                                </Paper>
                            );
                        })}
                    </Box>
                </Box>
            )}
            </Box>

            {/* Contact Person Popover */}
            <Popover
                open={Boolean(anchorEl) && Boolean(contactPerson)}
                anchorEl={anchorEl}
                onClose={() => setAnchorEl(null)}
                disableRestoreFocus
                transformOrigin={{ vertical: "top", horizontal: "left" }}
                anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
                sx={{ pointerEvents: "none" }}
            >
                <Box sx={{ display: "grid", gap: 1, p: { xs: 1.25, md: 1.5 }, minWidth: 260 }}>
                    <Box>
                        <Typography sx={{ fontSize: { xs: 10, md: 12 }, color: "#6b7280" }}>Name</Typography>
                        <Typography sx={{ fontWeight: 600, fontSize: { xs: 12, md: 14 } }}>
                            {String(contactPerson || "")?.split("|")[0]?.trim() || "-"}
                        </Typography>
                    </Box>
                    <Box>
                        <Typography sx={{ fontSize: { xs: 10, md: 12 }, color: "#6b7280" }}>Email Id</Typography>
                        <Typography sx={{ fontWeight: 600, fontSize: { xs: 12, md: 14 } }}>{String(contactPerson || "")?.split("|")[2]?.trim() || "-"}</Typography>
                    </Box>
                    <Box>
                        <Typography sx={{ fontSize: { xs: 10, md: 12 }, color: "#6b7280" }}>Mobile Number</Typography>
                        <Typography sx={{ fontWeight: 600, fontSize: { xs: 12, md: 14 } }}>{String(contactPerson || "")?.split("|")[1]?.trim() || "-"}</Typography>
                    </Box>
                </Box>
            </Popover>
        </Paper>
        </ThemeProvider>
    );
};

export default ClaimStatusCard;


